import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Building2, Plus, Check, X, Clock, Loader2, Pencil, Trash2, ChevronDown, ChevronUp, Users, Eye, EyeOff, Timer, CreditCard, FileText, Send, Image, ExternalLink, Banknote, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateFr(d) {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
}

export default function AdminAgences() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [agencies, setAgencies] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [availRequests, setAvailRequests] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [tab, setTab] = useState('requests');
  const [showForm, setShowForm] = useState(false);
  const [editAgency, setEditAgency] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, b, r, c, po] = await Promise.all([
        axios.get(`${API}/admin/agencies`, { headers }),
        axios.get(`${API}/admin/agency-bookings`, { headers }),
        axios.get(`${API}/admin/availability-requests`, { headers }),
        axios.get(`${API}/admin/partnership-contracts`, { headers }),
        axios.get(`${API}/admin/purchase-orders`, { headers }),
      ]);
      setAgencies(a.data);
      setBookings(b.data);
      setAvailRequests(r.data);
      setContracts(c.data);
      setPurchaseOrders(po.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, [headers, navigate]);

  useEffect(() => { load(); }, []);

  const approveBooking = async (id) => {
    try {
      await axios.put(`${API}/admin/agency-bookings/${id}/approve`, {}, { headers });
      toast.success('Réservation approuvée');
      load();
    } catch { toast.error('Erreur'); }
  };

  const rejectBooking = async (id, reason) => {
    try {
      await axios.put(`${API}/admin/agency-bookings/${id}/reject`, { admin_notes: reason || '' }, { headers });
      toast.success('Réservation refusée');
      load();
    } catch { toast.error('Erreur'); }
  };

  const deleteAgency = async (id) => {
    if (!window.confirm('Supprimer cette agence ?')) return;
    try {
      await axios.delete(`${API}/admin/agencies/${id}`, { headers });
      toast.success('Agence supprimée');
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#C4724F]" />
    </div>
  );

  const pendingCount = bookings.filter(b => b.status === 'pending').length;
  const pendingReqCount = availRequests.filter(r => r.status === 'pending').length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={() => navigate('/admin')} className="text-gray-400 hover:text-gray-600 text-sm shrink-0">&larr;</button>
            <div className="h-5 w-px bg-gray-200 hidden sm:block" />
            <Building2 className="h-5 w-5 text-[#C4724F] shrink-0" />
            <h1 className="text-base sm:text-lg font-bold text-gray-900 truncate">Agences Partenaires</h1>
          </div>
          <Button size="sm" className="bg-[#C4724F] hover:bg-[#a8603f] w-full sm:w-auto"
            onClick={() => { setEditAgency(null); setShowForm(true); }} data-testid="add-agency-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle agence
          </Button>
        </div>
      </div>

      {/* Tabs — scrollable on mobile */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 overflow-x-auto scrollbar-hide">
          <div className="flex gap-1 min-w-max">
          <button onClick={() => setTab('requests')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'requests' ? 'border-[#C4724F] text-[#C4724F]' : 'border-transparent text-gray-500'
            }`} data-testid="admin-tab-requests">
            Demandes dispo {pendingReqCount > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">{pendingReqCount}</span>}
          </button>
          <button onClick={() => setTab('bookings')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'bookings' ? 'border-[#C4724F] text-[#C4724F]' : 'border-transparent text-gray-500'
            }`} data-testid="admin-tab-bookings">
            Réservations {pendingCount > 0 && <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingCount}</span>}
          </button>
          <button onClick={() => setTab('agencies')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === 'agencies' ? 'border-[#C4724F] text-[#C4724F]' : 'border-transparent text-gray-500'
            }`} data-testid="admin-tab-agencies">
            Agences ({agencies.length})
          </button>
          <button onClick={() => setTab('contracts')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'contracts' ? 'border-[#C4724F] text-[#C4724F]' : 'border-transparent text-gray-500'
            }`} data-testid="admin-tab-contracts">
            <FileText className="h-3.5 w-3.5" /> Contrats
          </button>
          <button onClick={() => setTab('orders')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
              tab === 'orders' ? 'border-[#C4724F] text-[#C4724F]' : 'border-transparent text-gray-500'
            }`} data-testid="admin-tab-orders">
            <FileText className="h-3.5 w-3.5" /> BC / Factures
          </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        {/* Availability Requests Tab */}
        {tab === 'requests' && (
          <AvailabilityRequestsAdmin requests={availRequests} headers={headers} onRefresh={load} />
        )}

        {/* Bookings Tab */}
        {tab === 'bookings' && (
          <div className="space-y-4">
            {bookings.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400">Aucune demande de réservation agence</p>
              </div>
            ) : (
              bookings.map(b => (
                <AgencyBookingCard key={b.id} booking={b} onApprove={approveBooking} onReject={rejectBooking} />
              ))
            )}
          </div>
        )}

        {/* Agencies Tab */}
        {tab === 'agencies' && (
          <div className="space-y-4">
            {agencies.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">Aucune agence partenaire</p>
                <Button className="bg-[#C4724F] hover:bg-[#a8603f]" onClick={() => { setEditAgency(null); setShowForm(true); }}>
                  <Plus className="h-4 w-4 mr-1" /> Créer la première
                </Button>
              </div>
            ) : (
              agencies.map(a => (
                <div key={a.id} className="bg-white rounded-xl border p-4 sm:p-5" data-testid={`agency-card-${a.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      {a.logo_url ? (
                        <img src={a.logo_url} alt={a.name} className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded-lg border shrink-0" />
                      ) : (
                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-[#C4724F]/10 rounded-lg flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-[#C4724F]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{a.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">{a.email} &middot; {a.commission_rate}%</p>
                        {a.contact_name && <p className="text-xs text-gray-400 truncate">{a.contact_name} {a.phone && `• ${a.phone}`}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-center">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                        {a.status === 'active' ? 'Actif' : 'Inactif'}
                      </span>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditAgency(a); setShowForm(true); }}>
                        <Pencil className="h-4 w-4 text-gray-400" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteAgency(a.id)}>
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Contracts Tab */}
        {tab === 'contracts' && (
          <ContractManagerTab contracts={contracts} agencies={agencies} headers={headers} onRefresh={load} />
        )}

        {/* Purchase Orders / Invoices Tab */}
        {tab === 'orders' && (
          <AdminPurchaseOrdersTab orders={purchaseOrders} headers={headers} onRefresh={load} />
        )}
      </div>

      {/* Agency Form Modal */}
      {showForm && (
        <AgencyFormModal
          agency={editAgency}
          headers={headers}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

// --- Agency Booking Card ---
function AgencyBookingCard({ booking: b, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const statusConfig = {
    pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800', icon: Clock },
    approved: { label: 'Approuvée', cls: 'bg-emerald-100 text-emerald-800', icon: Check },
    rejected: { label: 'Refusée', cls: 'bg-red-100 text-red-800', icon: X },
  };
  const s = statusConfig[b.status] || statusConfig.pending;

  return (
    <div className={`bg-white rounded-xl border ${b.status === 'pending' ? 'border-amber-200 shadow-sm' : ''}`} data-testid={`admin-agency-booking-${b.id}`}>
      <div className="p-4 sm:p-5 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3 sm:gap-4 min-w-0">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${b.status === 'pending' ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <s.icon className={`h-4 w-4 sm:h-5 sm:w-5 ${b.status === 'pending' ? 'text-amber-600' : b.status === 'approved' ? 'text-emerald-600' : 'text-red-500'}`} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{b.customer_name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.cls}`}>{s.label}</span>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">{b.cruise_name} &middot; {b.start_date} → {b.end_date}</p>
              <p className="text-xs text-gray-400">{b.agency_name} &middot; {b.cabin_count} cab. &middot; {b.total_amount}€</p>
            </div>
          </div>
          <div className="flex items-center gap-2 self-end sm:self-center">
            {b.status === 'pending' && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
                  onClick={(e) => { e.stopPropagation(); onApprove(b.id); }} data-testid={`approve-${b.id}`}>
                  <Check className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Approuver</span>
                </Button>
                <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 h-8 text-xs"
                  onClick={(e) => { e.stopPropagation(); setShowReject(true); setExpanded(true); }} data-testid={`reject-btn-${b.id}`}>
                  <X className="h-3.5 w-3.5 sm:mr-1" /> <span className="hidden sm:inline">Refuser</span>
                </Button>
              </>
            )}
            {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-5 pb-5 border-t pt-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-gray-400 block text-xs">Email client</span> {b.customer_email || '-'}</div>
            <div><span className="text-gray-400 block text-xs">Téléphone</span> {b.customer_phone || '-'}</div>
            <div><span className="text-gray-400 block text-xs">Commission ({b.commission_rate}%)</span> <span className="font-semibold text-[#C4724F]">{b.commission_amount}€</span></div>
            <div><span className="text-gray-400 block text-xs">Part SOGNUDIMARE</span> <span className="font-semibold">{(b.total_amount - b.commission_amount).toFixed(0)}€</span></div>
          </div>
          {b.notes && <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3"><span className="font-medium">Notes :</span> {b.notes}</p>}
          {b.admin_notes && <p className="text-sm text-gray-600 bg-blue-50 rounded-lg p-3"><span className="font-medium">Note admin :</span> {b.admin_notes}</p>}
          {showReject && b.status === 'pending' && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs text-gray-500 block mb-1">Raison du refus (optionnel)</label>
                <Input value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Raison..." />
              </div>
              <Button size="sm" variant="destructive" onClick={() => onReject(b.id, rejectReason)} data-testid={`confirm-reject-${b.id}`}>
                Confirmer le refus
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Agency Form Modal ---
function AgencyFormModal({ agency, headers, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: agency?.name || '',
    email: agency?.email || '',
    password: '',
    contact_name: agency?.contact_name || '',
    phone: agency?.phone || '',
    logo_url: agency?.logo_url || '',
    commission_rate: agency?.commission_rate || 10,
    commission_type: agency?.commission_type || 'deduct',
    status: agency?.status || 'active',
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Nom et email requis'); return; }
    if (!agency && !form.password) { toast.error('Mot de passe requis'); return; }
    setSubmitting(true);
    try {
      if (agency) {
        await axios.put(`${API}/admin/agencies/${agency.id}`, form, { headers });
        toast.success('Agence mise à jour');
      } else {
        await axios.post(`${API}/admin/agencies`, form, { headers });
        toast.success('Agence créée');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="agency-form-modal">
        <div className="bg-[#0A2540] text-white p-6 rounded-t-2xl">
          <h2 className="font-bold text-lg">{agency ? 'Modifier l\'agence' : 'Nouvelle agence partenaire'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Nom de l'agence *</label>
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required data-testid="agency-form-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Email *</label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required data-testid="agency-form-email" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">
              Mot de passe {agency ? '(laisser vide pour ne pas changer)' : '*'}
            </label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} value={form.password}
                onChange={e => setForm(f => ({...f, password: e.target.value}))}
                required={!agency} data-testid="agency-form-password" />
              <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Contact</label>
              <Input value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} data-testid="agency-form-contact" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Téléphone</label>
              <Input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} data-testid="agency-form-phone" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Logo de l'agence</label>
            {form.logo_url && (
              <div className="mb-2 flex items-center gap-3">
                <img src={form.logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url}
                  alt="Logo" className="h-12 w-auto rounded border object-contain bg-white" />
                <button type="button" onClick={() => setForm(f => ({...f, logo_url: ''}))} className="text-xs text-red-500 hover:underline">Supprimer</button>
              </div>
            )}
            <label className="flex items-center gap-2 cursor-pointer border-2 border-dashed border-gray-300 rounded-lg p-3 hover:border-[#C4724F] transition-colors" data-testid="agency-logo-upload">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 5 Mo)'); return; }
                const fd = new FormData();
                fd.append('file', file);
                try {
                  const res = await axios.post(`${API}/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
                  setForm(f => ({...f, logo_url: res.data.url}));
                  toast.success('Logo téléchargé');
                } catch (err) { toast.error('Erreur upload'); }
              }} />
              <Image className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-500">{form.logo_url ? 'Changer le logo' : 'Télécharger le logo (PNG, JPG)'}</span>
            </label>
          </div>
          <div className="rounded-xl border-2 border-[#C4724F]/30 bg-gradient-to-br from-[#FFF7F2] to-[#FFEDE0] p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-[#C4724F] text-white font-bold text-sm">%</span>
              <div>
                <label className="text-sm font-bold text-[#0A2540] block leading-tight">Taux de commission de l'agence</label>
                <p className="text-[11px] text-[#7A5A48] leading-tight">Pourcentage reversé à l'agence sur chaque réservation</p>
              </div>
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={form.commission_rate}
                  onChange={e => setForm(f => ({...f, commission_rate: parseFloat(e.target.value) || 0}))}
                  data-testid="agency-form-commission"
                  className="text-2xl font-display font-bold text-[#C4724F] pr-12 h-14 border-2 border-[#C4724F]/40 bg-white"
                  required
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-[#C4724F]">%</span>
              </div>
              <div className="flex gap-1">
                {[5, 10, 15, 20].map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setForm(f => ({...f, commission_rate: v}))}
                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                      form.commission_rate === v
                        ? 'bg-[#C4724F] text-white shadow-md'
                        : 'bg-white border border-[#C4724F]/30 text-[#C4724F] hover:bg-[#FFEDE0]'
                    }`}
                    data-testid={`commission-preset-${v}`}
                  >
                    {v}%
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-[#7A5A48] mt-2">
              <strong>Exemple :</strong> sur une réservation de 5 000€ à {form.commission_rate || 0}% → l'agence reçoit <strong className="text-[#C4724F]">{Math.round(5000 * (form.commission_rate || 0) / 100)}€</strong> de commission.
            </p>
          </div>

          <div>
            <label className="text-sm font-bold text-[#0A2540] block mb-2">Mode d'application de la commission</label>
            <div className="grid sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setForm(f => ({...f, commission_type: 'deduct'}))}
                className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                  form.commission_type === 'deduct'
                    ? 'border-[#2A9D8F] bg-[#2A9D8F]/5 shadow-md ring-2 ring-[#2A9D8F]/20'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid="commission-type-deduct"
              >
                {form.commission_type === 'deduct' && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#2A9D8F] text-white text-xs flex items-center justify-center font-bold">✓</span>
                )}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-lg">📉</span>
                  <span className="font-bold text-sm text-[#0A2540]">Commission DÉDUITE</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-snug mb-2">
                  L'agence vend au prix SOGNUDIMARE. La commission est <strong>déduite</strong> des tarifs et reversée à l'agence.
                </p>
                <div className="text-[10px] text-slate-500 bg-slate-50 rounded px-2 py-1 border border-slate-200">
                  Client paie : <strong>2 560€</strong><br />
                  Agence reçoit : <strong className="text-[#2A9D8F]">{Math.round(2560 * (form.commission_rate || 0) / 100)}€</strong> de votre part
                </div>
              </button>

              <button
                type="button"
                onClick={() => setForm(f => ({...f, commission_type: 'markup'}))}
                className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                  form.commission_type === 'markup'
                    ? 'border-[#C4724F] bg-[#C4724F]/5 shadow-md ring-2 ring-[#C4724F]/20'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
                data-testid="commission-type-markup"
              >
                {form.commission_type === 'markup' && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#C4724F] text-white text-xs flex items-center justify-center font-bold">✓</span>
                )}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-lg">📈</span>
                  <span className="font-bold text-sm text-[#0A2540]">Commission AJOUTÉE</span>
                </div>
                <p className="text-[11px] text-slate-600 leading-snug mb-2">
                  L'agence <strong>ajoute</strong> sa commission au prix SOGNUDIMARE. Le client paie plus cher, l'agence garde la différence.
                </p>
                <div className="text-[10px] text-slate-500 bg-slate-50 rounded px-2 py-1 border border-slate-200">
                  Client paie : <strong>{2560 + Math.round(2560 * (form.commission_rate || 0) / 100)}€</strong><br />
                  Agence garde : <strong className="text-[#C4724F]">{Math.round(2560 * (form.commission_rate || 0) / 100)}€</strong> de différentiel
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Statut</label>
              <select className="w-full border rounded-md h-10 px-3 text-sm" value={form.status}
                onChange={e => setForm(f => ({...f, status: e.target.value}))} data-testid="agency-form-status">
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
              </select>
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button type="submit" className="flex-1 bg-[#C4724F] hover:bg-[#a8603f]" disabled={submitting} data-testid="agency-form-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (agency ? 'Mettre à jour' : 'Créer l\'agence')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


// --- Availability Requests Admin Component ---
const REQ_STATUS = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Confirmé', cls: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Refusé', cls: 'bg-red-100 text-red-700' },
  expired: { label: 'Expiré', cls: 'bg-gray-100 text-gray-500' },
  confirmed: { label: 'Payé', cls: 'bg-blue-100 text-blue-700' },
};

function AvailabilityRequestsAdmin({ requests, headers, onRefresh }) {
  const [responding, setResponding] = useState(null);
  const [responseNote, setResponseNote] = useState('');
  const [extending, setExtending] = useState(null);
  const [payingIdx, setPayingIdx] = useState(null);

  const approve = async (id) => {
    setResponding(id);
    try {
      await axios.put(`${API}/admin/availability-requests/${id}/approve`,
        { admin_response: responseNote || 'Disponibilité confirmée' }, { headers });
      toast.success('Disponibilité confirmée ! Places bloquées 3 jours.');
      setResponseNote('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setResponding(null); }
  };

  const reject = async (id) => {
    if (!responseNote.trim()) { toast.error('Indiquez la raison du refus'); return; }
    setResponding(id);
    try {
      await axios.put(`${API}/admin/availability-requests/${id}/reject`,
        { admin_response: responseNote }, { headers });
      toast.success('Demande refusée');
      setResponseNote('');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setResponding(null); }
  };

  const extend = async (id, days = 3) => {
    setExtending(id);
    try {
      await axios.put(`${API}/admin/availability-requests/${id}/extend`,
        { extra_days: days }, { headers });
      toast.success(`Délai prolongé de ${days} jours`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setExtending(null); }
  };

  const confirmPayment = async (reqId, paymentIndex, method) => {
    setPayingIdx(`${reqId}-${paymentIndex}`);
    try {
      await axios.put(`${API}/admin/availability-requests/${reqId}/confirm-payment`,
        { payment_index: paymentIndex, method }, { headers });
      toast.success('Paiement enregistré');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setPayingIdx(null); }
  };

  const generateSquareLink = async (reqId, paymentIndex) => {
    setPayingIdx(`sq-${reqId}-${paymentIndex}`);
    try {
      const res = await axios.post(`${API}/admin/availability-requests/${reqId}/generate-square-link`,
        { payment_index: paymentIndex }, { headers });
      navigator.clipboard.writeText(res.data.payment_link);
      toast.success('Lien Square créé et copié !');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur création lien'); }
    finally { setPayingIdx(null); }
  };

  const deleteRequest = async (reqId) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await axios.delete(`${API}/admin/availability-requests/${reqId}`, { headers });
      toast.success('Demande supprimée');
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  if (requests.length === 0) return (
    <div className="bg-white rounded-xl border p-12 text-center">
      <Timer className="h-12 w-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-400">Aucune demande de disponibilité</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {requests.map(req => {
        const st = REQ_STATUS[req.status] || REQ_STATUS.pending;
        const holdUntil = req.hold_until ? new Date(req.hold_until) : null;
        const now = new Date();
        const holdRemaining = holdUntil ? Math.max(0, Math.floor((holdUntil - now) / 3600000)) : 0;

        return (
          <div key={req.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`admin-req-${req.id}`}>
            <div className="p-5">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                    <span className="text-sm font-bold text-gray-900">{req.agency_name}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-sm text-gray-700">{req.cruise_name}</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {formatDateFr(req.start_date)} → {formatDateFr(req.end_date)} — {req.passenger_count} pax{req.is_solo ? ' (Solo)' : ''} — {req.cabins_needed} cab.
                  </p>
                  {req.client_name && <p className="text-xs text-gray-400 mt-0.5">Client : {req.client_name}</p>}
                  {req.message && <p className="text-xs text-gray-500 mt-1 italic bg-gray-50 p-2 rounded">"{req.message}"</p>}
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-[#C4724F]">{req.total_sognudimare}€</span>
                  <p className="text-xs text-gray-400">Prix Sognudimare</p>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-400 hover:text-red-600 mt-1"
                    onClick={() => deleteRequest(req.id)} data-testid={`delete-req-${req.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>

              {/* Pending — response form */}
              {req.status === 'pending' && (
                <div className="mt-4 border-t pt-4 space-y-3">
                  <Textarea placeholder="Note pour l'agence (obligatoire pour un refus)..."
                    value={responseNote}
                    onChange={e => setResponseNote(e.target.value)}
                    rows={2} className="text-sm" data-testid={`req-note-${req.id}`} />
                  <div className="flex gap-3">
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => approve(req.id)}
                      disabled={responding === req.id} data-testid={`approve-req-${req.id}`}>
                      {responding === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Confirmer (bloquer 3j)</>}
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => reject(req.id)}
                      disabled={responding === req.id} data-testid={`reject-req-${req.id}`}>
                      <X className="h-4 w-4 mr-1" /> Refuser
                    </Button>
                  </div>
                </div>
              )}

              {/* Approved — hold countdown + extend + payment */}
              {req.status === 'approved' && (
                <div className="mt-4 border-t pt-4 space-y-3">
                  {holdUntil && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-amber-50 rounded-lg p-3 border border-amber-200 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Timer className="h-4 w-4 text-amber-600 shrink-0" />
                        <span className="text-xs sm:text-sm text-amber-800">
                          Places bloquées — {holdRemaining > 24 ? `${Math.floor(holdRemaining/24)}j ${holdRemaining%24}h` : `${holdRemaining}h`} restantes
                        </span>
                        {req.hold_extended && <span className="text-xs bg-amber-200 text-amber-700 px-1.5 py-0.5 rounded shrink-0">Prolongé</span>}
                      </div>
                      <Button size="sm" variant="outline" onClick={() => extend(req.id)}
                        disabled={extending === req.id} className="h-7 text-xs self-start sm:self-auto shrink-0">
                        {extending === req.id ? <Loader2 className="h-3 w-3 animate-spin" /> : '+3 jours'}
                      </Button>
                    </div>
                  )}
                  {/* Payment schedule */}
                  {req.payment_schedule?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase">Échéancier de paiement</p>
                      {req.payment_schedule.map((p, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm py-1.5 gap-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-600">{p.label}</span>
                            <span className="font-medium">{p.amount}€</span>
                            <span className="text-xs text-gray-400">avant {formatDateFr(p.due_date)}</span>
                          </div>
                          {p.status === 'paid' ? (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1 self-start sm:self-auto">
                              <Check className="h-3 w-3" /> Payé {p.method === 'virement' ? '(virement)' : '(Square)'}
                            </span>
                          ) : (
                            <div className="flex gap-1 items-center flex-wrap">
                              {p.square_link ? (
                                <a href={p.square_link} target="_blank" rel="noreferrer"
                                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center gap-1">
                                  <ExternalLink className="h-3 w-3" /> Lien envoyé
                                </a>
                              ) : (
                                <Button size="sm" className="h-7 text-xs bg-[#0A2540]"
                                  onClick={() => generateSquareLink(req.id, i)}
                                  disabled={payingIdx === `sq-${req.id}-${i}`}>
                                  {payingIdx === `sq-${req.id}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3 mr-1" /> Square</>}
                                </Button>
                              )}
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => confirmPayment(req.id, i, 'square')}
                                disabled={payingIdx === `${req.id}-${i}`}>
                                {payingIdx === `${req.id}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> CB</>}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => confirmPayment(req.id, i, 'virement')}
                                disabled={payingIdx === `${req.id}-${i}`}>
                                Virement
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      <p className="text-xs text-gray-400 mt-1">Paiement intégral requis 30 jours avant le départ</p>
                      <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1"><Banknote className="h-3 w-3" /> RIB pour virement</p>
                        <div className="text-xs text-blue-900 space-y-0.5 font-mono">
                          <p>IBAN : FR76 2573 3000 0100 0001 4479 108</p>
                          <p>BIC : PSSSFR22</p>
                          <p className="font-sans text-blue-600">GREEN-GOT (La banque à impact) — NICOLAS LLORENS EI</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Confirmed (fully paid) */}
              {req.status === 'confirmed' && (
                <div className="mt-3 bg-emerald-50 rounded-lg p-3 border border-emerald-200 flex items-center gap-2">
                  <Check className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">Paiement complet — Réservation confirmée</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ── Contract Manager Tab ──

const CONTRACT_STATUS = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700' },
  signed_armateur: { label: 'Signé (Armateur)', cls: 'bg-blue-100 text-blue-700' },
  sent: { label: 'Envoyé', cls: 'bg-amber-100 text-amber-700' },
  signed_agency: { label: 'Signé (Agence)', cls: 'bg-purple-100 text-purple-700' },
  active: { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' },
};

function ContractManagerTab({ contracts, agencies, headers, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState('');
  const [formData, setFormData] = useState({
    armateur_name: 'SOGNUDIMARE SARL',
    armateur_address: 'Port de plaisance, 20000 Ajaccio, Corse',
    armateur_siret: '451 387 369 00031',
    armateur_ape: '52.22Z',
    armateur_representative: 'Nicolas LLORENS',
    agency_address: '',
    agency_siret: '',
    agency_ape: '',
    agency_licence: '',
    agency_representative: '',
    duration: '1 an',
  });
  const [signingId, setSigningId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  const startDraw = (e) => {
    drawingRef.current = true;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * scaleX;
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * scaleY;
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0A2540';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const endDraw = () => { drawingRef.current = false; };

  const createContract = async () => {
    if (!selectedAgency) return toast.error('Sélectionnez une agence');
    setCreating(true);
    try {
      await axios.post(`${API}/admin/partnership-contracts`, {
        agency_id: selectedAgency,
        ...formData,
      }, { headers });
      toast.success('Contrat créé');
      setSelectedAgency('');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setCreating(false); }
  };

  const signContract = async (contractId) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let hasDrawing = false;
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) { hasDrawing = true; break; }
    }
    if (!hasDrawing) return toast.error('Veuillez signer');

    try {
      const sigData = canvas.toDataURL('image/png');
      await axios.post(`${API}/admin/partnership-contracts/${contractId}/sign`, {
        signature: sigData,
      }, { headers });
      toast.success('Contrat signé (Armateur)');
      setSigningId(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const sendContract = async (contractId) => {
    setSendingId(contractId);
    try {
      const res = await axios.post(`${API}/admin/partnership-contracts/${contractId}/send`, {}, { headers });
      toast.success('Contrat envoyé à l\'agence');
      if (res.data.sign_url) {
        navigator.clipboard?.writeText(res.data.sign_url);
        toast.success('Lien de signature copié !');
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setSendingId(null); }
  };

  const downloadPdf = (contractId) => {
    window.open(`${API}/admin/partnership-contracts/${contractId}/pdf?token=${localStorage.getItem('token')}`, '_blank');
  };

  const resetContract = async (contractId) => {
    if (!window.confirm('Remettre le contrat en brouillon ? Toutes les signatures seront effacées et un nouveau lien sera généré.')) return;
    try {
      await axios.post(`${API}/admin/partnership-contracts/${contractId}/reset`, {}, { headers });
      toast.success('Contrat remis en brouillon — vous pouvez le modifier et re-signer');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  // Filter agencies without active contracts
  const agenciesWithoutContract = agencies.filter(
    a => !contracts.some(c => c.agency_id === a.id && ['draft', 'sent', 'signed_armateur', 'signed_agency', 'active'].includes(c.status))
  );

  return (
    <div className="space-y-6">
      {/* Create new contract */}
      <div className="bg-white rounded-xl border p-6" data-testid="create-contract-section">
        <h3 className="font-bold text-gray-900 mb-4">Nouveau contrat de partenariat</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Agence</label>
            <select value={selectedAgency} onChange={e => setSelectedAgency(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="contract-agency-select">
              <option value="">Sélectionner une agence</option>
              {agenciesWithoutContract.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Durée</label>
            <select value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="contract-duration-select">
              <option value="1 an">1 an</option>
              <option value="2 ans">2 ans</option>
              <option value="3 ans">3 ans</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Nom Armateur</label>
            <Input value={formData.armateur_name} onChange={e => setFormData({...formData, armateur_name: e.target.value})}
              data-testid="contract-armateur-name" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Adresse Armateur</label>
            <Input value={formData.armateur_address} onChange={e => setFormData({...formData, armateur_address: e.target.value})}
              placeholder="Port de plaisance, Ajaccio" data-testid="contract-armateur-address" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">SIRET Armateur</label>
            <Input value={formData.armateur_siret} onChange={e => setFormData({...formData, armateur_siret: e.target.value})}
              data-testid="contract-armateur-siret" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">APE Armateur</label>
            <Input value={formData.armateur_ape} onChange={e => setFormData({...formData, armateur_ape: e.target.value})}
              data-testid="contract-armateur-ape" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Représentant Armateur</label>
            <Input value={formData.armateur_representative} onChange={e => setFormData({...formData, armateur_representative: e.target.value})}
              placeholder="Nom du capitaine" data-testid="contract-armateur-rep" />
          </div>
        </div>

        <div className="border-t mt-4 pt-4">
          <p className="text-xs text-gray-500 mb-3">Informations de l'agence (optionnel, peut être modifié avant envoi)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Adresse Agence</label>
              <Input value={formData.agency_address} onChange={e => setFormData({...formData, agency_address: e.target.value})}
                data-testid="contract-agency-address" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">SIRET Agence</label>
              <Input value={formData.agency_siret} onChange={e => setFormData({...formData, agency_siret: e.target.value})}
                data-testid="contract-agency-siret" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">APE Agence</label>
              <Input value={formData.agency_ape} onChange={e => setFormData({...formData, agency_ape: e.target.value})}
                data-testid="contract-agency-ape" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Licence Agence</label>
              <Input value={formData.agency_licence} onChange={e => setFormData({...formData, agency_licence: e.target.value})}
                placeholder="IM069120014" data-testid="contract-agency-licence" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Représentant Agence</label>
              <Input value={formData.agency_representative} onChange={e => setFormData({...formData, agency_representative: e.target.value})}
                data-testid="contract-agency-rep" />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Button onClick={createContract} disabled={creating || !selectedAgency}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="create-contract-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Créer le contrat
          </Button>
        </div>
      </div>

      {/* Existing contracts */}
      <div className="space-y-4">
        <h3 className="font-bold text-gray-900">Contrats existants</h3>
        {contracts.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400">Aucun contrat de partenariat</p>
          </div>
        ) : (
          contracts.map(c => {
            const st = CONTRACT_STATUS[c.status] || CONTRACT_STATUS.draft;
            return (
              <div key={c.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`contract-row-${c.id}`}>
                <div className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        <span className="text-sm font-bold text-[#0A2540]">{c.agency_name}</span>
                        <span className="text-xs text-gray-400 font-mono">{c.reference}</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Créé le {formatDateFr(c.created_at?.split('T')[0])} — Durée : {c.duration}
                      </p>
                      {c.armateur_signed_at && (
                        <p className="text-xs text-green-600 mt-1">Signé par l'Armateur le {formatDateFr(c.armateur_signed_at?.split('T')[0])}</p>
                      )}
                      {c.agency_signed_at && (
                        <p className="text-xs text-green-600">Signé par l'Agence le {formatDateFr(c.agency_signed_at?.split('T')[0])}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Download PDF */}
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1"
                        onClick={() => downloadPdf(c.id)} data-testid={`contract-pdf-${c.id}`}>
                        <FileText className="h-3.5 w-3.5" /> PDF
                      </Button>

                      {/* Sign (Armateur) if not yet signed */}
                      {!c.armateur_signature && (
                        <Button size="sm" className="h-8 text-xs gap-1 bg-[#0A2540] hover:bg-[#0A2540]/90"
                          onClick={() => setSigningId(signingId === c.id ? null : c.id)}
                          data-testid={`contract-sign-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Signer
                        </Button>
                      )}

                      {/* Re-sign (if already signed but want to redo) */}
                      {c.armateur_signature && !c.agency_signature && (
                        <Button variant="outline" size="sm" className="h-8 text-xs gap-1"
                          onClick={() => setSigningId(signingId === c.id ? null : c.id)}
                          data-testid={`contract-resign-${c.id}`}>
                          <Pencil className="h-3.5 w-3.5" /> Re-signer
                        </Button>
                      )}

                      {/* Send to agency (only if armateur signed) */}
                      {c.armateur_signature && !c.agency_signature && (
                        <Button size="sm" className="h-8 text-xs gap-1 bg-[#C4724F] hover:bg-[#a8603f]"
                          onClick={() => sendContract(c.id)} disabled={sendingId === c.id}
                          data-testid={`contract-send-${c.id}`}>
                          {sendingId === c.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                          {c.status === 'sent' ? 'Renvoyer' : 'Envoyer'}
                        </Button>
                      )}

                      {/* Reset to draft (modify) — only if agency hasn't signed yet */}
                      {c.status !== 'draft' && !c.agency_signature && (
                        <Button variant="ghost" size="sm" className="h-8 text-xs gap-1 text-gray-500 hover:text-red-600"
                          onClick={() => resetContract(c.id)}
                          data-testid={`contract-reset-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Modifier
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Signing section */}
                  {signingId === c.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-xl border space-y-3" data-testid={`contract-sign-pad-${c.id}`}>
                      <p className="text-sm font-medium text-gray-700">Signature de l'Armateur</p>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
                        <canvas
                          ref={canvasRef}
                          width={500}
                          height={150}
                          className="w-full touch-none cursor-crosshair"
                          onMouseDown={startDraw}
                          onMouseMove={draw}
                          onMouseUp={endDraw}
                          onMouseLeave={endDraw}
                          onTouchStart={startDraw}
                          onTouchMove={draw}
                          onTouchEnd={endDraw}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => signContract(c.id)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 gap-1"
                          data-testid={`contract-confirm-sign-${c.id}`}>
                          <Check className="h-3.5 w-3.5" /> Confirmer
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => {
                          const ctx = canvasRef.current?.getContext('2d');
                          if (ctx) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                        }}>Effacer</Button>
                        <Button variant="ghost" size="sm" onClick={() => setSigningId(null)}>Annuler</Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}


// ── Admin Purchase Orders / Invoices Tab ──
function AdminPurchaseOrdersTab({ orders, headers, onRefresh }) {
  const [downloading, setDownloading] = useState(null);
  const [payingIdx, setPayingIdx] = useState(null);
  const [sendingReminder, setSendingReminder] = useState(null);

  const handlePdf = async (order) => {
    setDownloading(order.id);
    try {
      const resp = await axios.get(`${API}/admin/purchase-orders/${order.id}/pdf`, {
        headers, responseType: 'blob'
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Erreur téléchargement PDF');
    } finally {
      setDownloading(null);
    }
  };

  const confirmPayment = async (orderId, paymentIndex, method) => {
    const key = `${orderId}-${paymentIndex}`;
    setPayingIdx(key);
    try {
      await axios.put(`${API}/admin/purchase-orders/${orderId}/confirm-payment`, {
        payment_index: paymentIndex, method
      }, { headers });
      toast.success('Paiement confirmé');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setPayingIdx(null);
    }
  };

  const generateSquareLink = async (orderId, paymentIndex) => {
    const key = `sq-${orderId}-${paymentIndex}`;
    setPayingIdx(key);
    try {
      await axios.post(`${API}/admin/purchase-orders/${orderId}/generate-square-link`, {
        payment_index: paymentIndex
      }, { headers });
      toast.success('Lien Square créé');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur Square');
    } finally {
      setPayingIdx(null);
    }
  };

  const sendReminder = async (orderId) => {
    setSendingReminder(orderId);
    try {
      await axios.post(`${API}/admin/purchase-orders/${orderId}/reminder`, {}, { headers });
      toast.success('Relance envoyée');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSendingReminder(null);
    }
  };

  const cancelOrder = async (orderId) => {
    if (!window.confirm('Annuler ce bon de commande ?')) return;
    try {
      await axios.put(`${API}/admin/purchase-orders/${orderId}/cancel`, {}, { headers });
      toast.success('Bon de commande annulé');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const STATUS = {
    en_attente: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
    paye: { label: 'Facture acquittée', cls: 'bg-emerald-100 text-emerald-700' },
    annule: { label: 'Annulé', cls: 'bg-red-100 text-red-700' },
  };

  const formatDateFrLocal = (d) => {
    if (!d) return '';
    const parts = String(d).split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
  };

  return (
    <div className="space-y-4" data-testid="admin-purchase-orders-tab">
      <h2 className="text-lg font-bold text-gray-900">Bons de commande / Factures</h2>

      {orders.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400">Aucun bon de commande</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const st = STATUS[order.status] || STATUS.en_attente;
            const isPaid = order.status === 'paye';
            const isCancelled = order.status === 'annule';
            const docNumber = isPaid ? order.invoice_number : order.order_number;
            const schedule = order.payment_schedule || [];
            const paidCount = schedule.filter(s => s.status === 'paid').length;

            return (
              <div key={order.id} className="bg-white rounded-xl border overflow-hidden" data-testid={`admin-po-${order.id}`}>
                <div className={`h-1.5 ${isPaid ? 'bg-emerald-500' : isCancelled ? 'bg-red-400' : 'bg-amber-400'}`} />
                <div className="p-4 sm:p-5 space-y-4">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-[#0A2540]">{docNumber}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                        <span className="text-xs text-gray-400">{order.agency_name}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mt-1">{order.cruise_name}</p>
                      <p className="text-xs text-gray-500">
                        {formatDateFrLocal(order.start_date)} → {formatDateFrLocal(order.end_date)}
                        {' '}&middot; {order.passenger_count} pax &middot; Client : {order.client?.name || '-'}
                      </p>
                      {isPaid && order.invoice_number && (
                        <p className="text-xs text-emerald-600 font-medium mt-1">Facture N° {order.invoice_number} &middot; Payé le {formatDateFrLocal(order.paid_at?.substring(0, 10))}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap self-end sm:self-start">
                      <span className="text-lg font-bold text-[#C4724F]">{order.total_ttc}€</span>
                      <Button size="sm" variant="outline" onClick={() => handlePdf(order)}
                        disabled={downloading === order.id} className="h-8 text-xs" data-testid={`admin-po-pdf-${order.id}`}>
                        {downloading === order.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Eye className="h-3.5 w-3.5 mr-1" /> PDF</>}
                      </Button>
                    </div>
                  </div>

                  {/* Passengers */}
                  {order.passengers?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Passagers</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                        {order.passengers.map((p, i) => (
                          <p key={i} className="text-xs text-gray-600">
                            {i+1}. <span className="font-medium">{p.last_name?.toUpperCase()} {p.first_name}</span>
                            <span className="text-gray-400 ml-1">— {formatDateFrLocal(p.date_of_birth)}, {p.place_of_birth}</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Schedule — Admin controls */}
                  {schedule.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-500 uppercase">Échéancier ({paidCount}/{schedule.length})</p>
                        {!isPaid && !isCancelled && (
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600"
                              onClick={() => sendReminder(order.id)}
                              disabled={sendingReminder === order.id} data-testid={`po-reminder-${order.id}`}>
                              {sendingReminder === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Relance'}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600"
                              onClick={() => cancelOrder(order.id)} data-testid={`po-cancel-${order.id}`}>
                              Annuler BC
                            </Button>
                          </div>
                        )}
                      </div>
                      {schedule.map((p, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-sm py-1.5 gap-2 border-b border-gray-100 last:border-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-600">{p.label}</span>
                            <span className="font-medium">{p.amount}€</span>
                            <span className="text-xs text-gray-400">avant {formatDateFrLocal(p.due_date)}</span>
                          </div>
                          {p.status === 'paid' ? (
                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1 self-start sm:self-auto">
                              <Check className="h-3 w-3" /> Payé {p.method === 'virement' ? '(virement)' : '(CB)'}
                            </span>
                          ) : !isCancelled ? (
                            <div className="flex gap-1 items-center flex-wrap">
                              {p.square_link ? (
                                <a href={p.square_link} target="_blank" rel="noreferrer"
                                  className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 flex items-center gap-1">
                                  <ExternalLink className="h-3 w-3" /> Lien envoyé
                                </a>
                              ) : (
                                <Button size="sm" className="h-7 text-xs bg-[#0A2540]"
                                  onClick={() => generateSquareLink(order.id, i)}
                                  disabled={payingIdx === `sq-${order.id}-${i}`} data-testid={`po-sq-${order.id}-${i}`}>
                                  {payingIdx === `sq-${order.id}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><CreditCard className="h-3 w-3 mr-1" /> Square</>}
                                </Button>
                              )}
                              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => confirmPayment(order.id, i, 'carte bancaire')}
                                disabled={payingIdx === `${order.id}-${i}`} data-testid={`po-pay-cb-${order.id}-${i}`}>
                                {payingIdx === `${order.id}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> PAYÉ CB</>}
                              </Button>
                              <Button size="sm" variant="outline" className="h-7 text-xs"
                                onClick={() => confirmPayment(order.id, i, 'virement')}
                                disabled={payingIdx === `${order.id}-${i}`} data-testid={`po-pay-vir-${order.id}-${i}`}>
                                PAYÉ Virement
                              </Button>
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
